/**
 * AnswerPane — right pane of the Matter AI Workspace.
 *
 * Renders the structured output Claude returned, validated server-side
 * against WorkspaceStructuredOutput.  No regex parsing, no markdown
 * splitting — every block is its own typed array and we render with
 * confidence.
 */
import { Badge } from "../../components/ui/badge";
import { AlertTriangle, FileText, Lightbulb, MessageCircleQuestion, ShieldAlert, Sparkles } from "lucide-react";

const SEVERITY_BADGE_VARIANTS = {
    low:    "bg-slate-100 text-slate-700 border-slate-200",
    medium: "bg-status-amber-dim text-status-amber border-amber-200",
    high:   "bg-status-red-dim text-status-red border-red-200",
};

export default function AnswerPane({ runResult, isLoading, errorMessage, fetchWarnings }) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64 text-slate-500" data-testid="aiworkspace-answer-loading">
                <Sparkles className="w-5 h-5 mr-2 animate-pulse text-teal" />
                Running…
            </div>
        );
    }
    if (errorMessage) {
        return (
            <div className="bg-status-red-dim border border-red-200 rounded-md p-4" data-testid="aiworkspace-answer-error">
                <div className="flex items-center gap-2 text-status-red font-semibold">
                    <AlertTriangle className="w-5 h-5" /> Run failed
                </div>
                <p className="mt-2 text-sm text-status-red whitespace-pre-wrap break-words">{errorMessage}</p>
            </div>
        );
    }
    if (!runResult) {
        return (
            <div className="text-sm text-slate-500 italic h-64 flex items-center justify-center"
                 data-testid="aiworkspace-answer-empty">
                Select an action and one or more documents, then press Run.
            </div>
        );
    }

    const { structured_output: output } = runResult;
    if (!output) {
        return (
            <div className="text-sm text-slate-500 italic" data-testid="aiworkspace-answer-empty">
                No structured output to display.
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="aiworkspace-answer">
            {/* Mandatory caveats — surfaced FIRST so the lawyer sees them before the suggestions. */}
            <div className="bg-amber-50 border-l-4 border-status-amber p-3 text-sm"
                 data-testid="aiworkspace-caveats">
                <div className="flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 text-status-amber flex-shrink-0 mt-0.5" />
                    <div className="text-status-amber">
                        <p className="font-semibold mb-1">Treat this as a starting point</p>
                        <p className="text-xs leading-relaxed">{output.caveats.plain_english_message}</p>
                    </div>
                </div>
            </div>

            {/* Doc-fetch warnings (e.g. truncation, unsupported format) */}
            {Array.isArray(fetchWarnings) && fetchWarnings.length > 0 && (
                <div className="bg-slate-100 border border-slate-200 rounded p-3 text-xs"
                     data-testid="aiworkspace-fetch-warnings">
                    <p className="font-semibold text-slate-700 mb-1">Document fetch notes</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-0.5">
                        {fetchWarnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                </div>
            )}

            {/* Summary */}
            <section data-testid="aiworkspace-summary">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Summary
                </h3>
                <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{output.summary}</p>
            </section>

            {/* Issues */}
            {output.issues.length > 0 && (
                <section data-testid="aiworkspace-issues">
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-status-amber" /> Issues ({output.issues.length})
                    </h3>
                    <ul className="space-y-2">
                        {output.issues.map((issue, i) => (
                            <li key={i} className="bg-navy-hover/40 border border-navy-border rounded p-3"
                                data-testid={`aiworkspace-issue-${i}`}>
                                <div className="flex items-start justify-between gap-2 mb-1.5">
                                    <span className="font-medium text-white text-sm">{issue.title}</span>
                                    <Badge variant="outline" className={`text-xs ${SEVERITY_BADGE_VARIANTS[issue.severity] || ""}`}>
                                        {issue.severity}
                                    </Badge>
                                </div>
                                <p className="text-sm text-slate-300 whitespace-pre-wrap">{issue.explanation}</p>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Suggested edits */}
            {output.suggested_edits.length > 0 && (
                <section data-testid="aiworkspace-suggested-edits">
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-teal" /> Suggested redlines ({output.suggested_edits.length})
                    </h3>
                    <ul className="space-y-3">
                        {output.suggested_edits.map((edit, i) => (
                            <li key={i} className="bg-navy-hover/40 border border-navy-border rounded p-3"
                                data-testid={`aiworkspace-edit-${i}`}>
                                <p className="text-xs font-medium text-slate-400 mb-2">{edit.location_hint}</p>
                                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                    <div className="bg-status-red-dim/30 border-l-2 border-status-red p-2 rounded">
                                        <p className="font-semibold text-status-red text-[10px] uppercase">Before</p>
                                        <p className="text-slate-200 mt-1 whitespace-pre-wrap">{edit.before_text || "(new clause)"}</p>
                                    </div>
                                    <div className="bg-teal-dim/30 border-l-2 border-teal p-2 rounded">
                                        <p className="font-semibold text-teal text-[10px] uppercase">After</p>
                                        <p className="text-slate-200 mt-1 whitespace-pre-wrap">{edit.after_text}</p>
                                    </div>
                                </div>
                                <p className="text-xs text-slate-300 italic">Rationale: {edit.rationale}</p>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Clarifying questions */}
            {output.questions_to_clarify.length > 0 && (
                <section data-testid="aiworkspace-questions">
                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                        <MessageCircleQuestion className="w-4 h-4 text-blue-400" /> Questions to clarify
                    </h3>
                    <ul className="space-y-2">
                        {output.questions_to_clarify.map((q, i) => (
                            <li key={i} className="text-sm text-slate-300" data-testid={`aiworkspace-question-${i}`}>
                                <p className="font-medium text-white">{q.question}</p>
                                <p className="text-xs text-slate-500 italic">{q.why_it_matters}</p>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {/* Sources used — every run cites which selected docs it relied on */}
            <section data-testid="aiworkspace-sources">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" /> Based on
                </h3>
                <ul className="text-xs text-slate-400 space-y-0.5">
                    {output.sources_used.map((src, i) => (
                        <li key={i} data-testid={`aiworkspace-source-${i}`}>
                            <span className="font-medium text-slate-300">{src.document_title}</span>
                            <span className="ml-2 italic">— {src.relevance_note}</span>
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
}
